import axios from 'axios';

// Centralized chart data service - fetches all data in one API call
class ChartDataService {
  constructor() {
    // Use localhost instead of 0.0.0.0 for browser compatibility
    this.baseURL = process.env.REACT_APP_MODEL_API_URL || 'http://localhost:8000/';
    this.cacheKey = 'gepredict_chart_data_cache';
    this.cacheExpiry = 60 * 60 * 1000; // 1 hour
  }

  // Fetch all chart data in one API call
  async fetchAllChartData() {
    try {
      console.log('Fetching all chart data from API...');
      const response = await axios.get(this.baseURL);
      const data = response.data;
      
      // NO CACHING - always fetch fresh data
      console.log('Chart data fetched successfully (no cache)');
      return this.parseChartData(data);
    } catch (error) {
      console.error('Error fetching chart data:', error);
      throw error;
    }
  }

  // Parse the API response into chart-specific data
  parseChartData(data) {
    // The API returns: [Isc_mod(), Voc_mod(), Vmpp_vec(), Impp_vec(), mpp_vec(), TMBS(), POAI_f(), validation_data]
    console.log('Parsing chart data:', data);
    console.log('Data length:', data.length);
    
    const parsedData = {
      Isc_mod: data[0] || [],
      Voc_mod: data[1] || [],
      Vmpp_vec: data[2] || [],
      Impp_vec: data[3] || [],
      mpp_vec: data[4] || [],
      TMBS: data[5] || [],
      POAI_f: data[6] || [],
      validation_data: data[7] || null  // Add validation data from index 7
    };
    
    console.log('Parsed chart data:', {
      Isc_mod_length: parsedData.Isc_mod.length,
      Voc_mod_length: parsedData.Voc_mod.length,
      Vmpp_vec_length: parsedData.Vmpp_vec.length,
      Impp_vec_length: parsedData.Impp_vec.length,
      mpp_vec_length: parsedData.mpp_vec.length,
      TMBS_length: parsedData.TMBS.length,
      POAI_f_length: parsedData.POAI_f.length,
      validation_data: parsedData.validation_data ? 'Found' : 'Not found'
    });
    
    return parsedData;
  }

  // Cache the data
  cacheData(data) {
    try {
      const cacheData = {
        data: data,
        timestamp: Date.now()
      };
      localStorage.setItem(this.cacheKey, JSON.stringify(cacheData));
      console.log('Chart data cached in localStorage for persistence across page refreshes');
    } catch (error) {
      console.warn('Failed to cache chart data:', error);
    }
  }

  // Load cached data
  loadCachedData() {
    try {
      const item = localStorage.getItem(this.cacheKey);
      if (item) {
        const parsed = JSON.parse(item);
        if (Date.now() - parsed.timestamp < this.cacheExpiry) {
          console.log('Loading chart data from localStorage cache (survives page refresh)');
          return this.parseChartData(parsed.data);
        } else {
          console.log('Chart data cache expired');
          this.clearCache();
        }
      }
    } catch (error) {
      console.warn('Failed to load cached chart data:', error);
    }
    return null;
  }

  // Clear cache
  clearCache() {
    try {
      localStorage.removeItem(this.cacheKey);
      console.log('Chart data cache cleared');
    } catch (error) {
      console.warn('Failed to clear chart data cache:', error);
    }
  }

  // Check if cache exists
  hasCachedData() {
    try {
      const item = localStorage.getItem(this.cacheKey);
      if (item) {
        const parsed = JSON.parse(item);
        return Date.now() - parsed.timestamp < this.cacheExpiry;
      }
    } catch (error) {
      console.warn('Error checking chart data cache:', error);
    }
    return false;
  }

  // Get data - always fetch fresh, no cache
  async getChartData() {
    // Always fetch fresh data from API
    console.log('Always fetching fresh data (cache disabled)');
    return await this.fetchAllChartData();
  }
}

// Create singleton instance
const chartDataService = new ChartDataService();

export default chartDataService; 